import { css } from '@emotion/react'
import styled from '@emotion/styled'
import React from 'react'

import { overrideOptional } from '@emico/overrides'
import { minWidth, BreakpointIdentifier, theme } from '@emico/styles'

interface ContainerBreakpointValues {
  width?: number
  offset?: number
}

type ContainerBreakpoints = {
  [key in BreakpointIdentifier]: ContainerBreakpointValues
}

type LayoutBreakpoints = {
  [key in BreakpointIdentifier]?: ContainerBreakpointValues
}

const initialContainerLayout: ContainerBreakpoints = {
  xs: {
    width: theme.containerMaxWidths.xs,
    offset: theme.containerOffsetWidths.xs,
  },
  sm: {
    width: theme.containerMaxWidths.sm,
    offset: theme.containerOffsetWidths.sm,
  },
  md: {
    width: theme.containerMaxWidths.md,
    offset: theme.containerOffsetWidths.md,
  },
  lg: {
    width: theme.containerMaxWidths.lg,
    offset: theme.containerOffsetWidths.lg,
  },
  xl: {
    width: theme.containerMaxWidths.xl,
    offset: theme.containerOffsetWidths.xl,
  },
}

interface WrapperProps {
  layout: ContainerBreakpoints
  noPadding: boolean
  fluid: boolean
}
const Wrapper = styled.div<WrapperProps>`
  width: 100%;
  margin: 0 auto;

  ${({ layout, fluid, noPadding }) =>
    Object.entries(layout).map(
      ([breakpoint, { width, offset }]) => css`
        @media ${minWidth(breakpoint as BreakpointIdentifier)} {
          max-width: ${fluid ? 'none' : `${width}px`};
          padding-left: ${noPadding ? '0px' : `${offset}px`};
          padding-right: ${noPadding ? '0px' : `${offset}px`};
        }
      `,
    )}
`

interface Props {
  layout?: LayoutBreakpoints
  noPadding?: boolean
  fluid?: boolean
  children: React.ReactNode
}

const Container = ({
  layout: overrideLayout,
  fluid = false,
  noPadding = false,
  children,
  ...others
}: Props) => {
  const mergedLayout = overrideLayout
    ? {
        ...initialContainerLayout,
        ...Object.fromEntries(
          Object.entries(overrideLayout).map(
            ([breakPointIdentifier, breakPointValues]) => [
              breakPointIdentifier,
              {
                width:
                  breakPointValues?.width ||
                  theme.containerMaxWidths[
                    breakPointIdentifier as BreakpointIdentifier
                  ],
                offset:
                  breakPointValues?.offset ||
                  theme.containerOffsetWidths[
                    breakPointIdentifier as BreakpointIdentifier
                  ],
              },
            ],
          ),
        ),
      }
    : { ...initialContainerLayout }

  return (
    <Wrapper
      fluid={fluid}
      noPadding={noPadding}
      layout={mergedLayout}
      {...others}
    >
      {children}
    </Wrapper>
  )
}

export default overrideOptional(Container)
